import _ from 'lodash';
import {
  IVScrollOffsetTop,
  IVScrollOffsetBottom,
  IVScrollRenderState,
  IVScrollSlot,
} from '@/modules/virtual-scroll/types';
import { DEFAULT_BUFFER_SIZE, DEFAULT_ITEM_SIZE } from '../../../constants';

import {
  IVScrollItem,
  IBaseVirtualScrollProps,
  IVScrollPosition,
} from '../../../types';
import { getItemIndices } from './helpers';

type IBaseVirtualScrollPropsIndexed = IBaseVirtualScrollProps & { itemIndicesById: Record<string | number, number>; };

export class BaseVirtualScroll {
  dataKey: string;
  buffer: number;
  defaultItemSize: number;

  viewportSize = 0;
  headerSlotSize = 0;
  footerSlotSize = 0;
  items: IVScrollItem[] = [];
  visibleItems: IVScrollItem[] = [];
  scrollTop = 0;
  itemSizesById: Record<number | string, number> = {};
  itemIndicesById: Record<number | string, number> = {};
  offsetTopDistanceBuffer = 0;
  pinnedItemId: string | number = '';
  pinnedItemPosition: IVScrollPosition = 'top';
  correctScrollOnResize = true;

  constructor(args: IBaseVirtualScrollPropsIndexed) {
    this.items = args.items;
    this.itemIndicesById = args.itemIndicesById,
    this.dataKey = args.dataKey;
    this.defaultItemSize = args.defaultItemSize ?? DEFAULT_ITEM_SIZE;
    this.buffer = args.buffer ?? DEFAULT_BUFFER_SIZE;
  }

  setPinnedItemId(itemId: string | number) {
    this.pinnedItemId = itemId;
  }

  setPinnedItemPosition(position: IVScrollPosition) {
    this.pinnedItemPosition = position;
  }

  isPinnedItemVisible() {
    if (!this.pinnedItemId) return false;
    return this.visibleItems.find(it => it[this.dataKey] === this.pinnedItemId);
  }

  setItemSize({
    resizedItemId,
    size,
    pinnedItemId,
  }: {
    resizedItemId: number | string;
    size: number;
    pinnedItemId?: number | string;
  }): IVScrollRenderState {
    const params: { scrollTop?: number; visibleItems?: IVScrollItem[]; } = {};
    const oldItemSize = this.getItemSizeById(resizedItemId);
    const localPinnedItemId = pinnedItemId ?? this.pinnedItemId;
    const sizeDiff = size - oldItemSize;
    if (this.correctScrollOnResize && sizeDiff) {
      const scrollTop = this.compensateScrollTop({
        resizedItemId,
        sizeDiff: size - oldItemSize,
        pinnedItemId: localPinnedItemId,
      });
      params.scrollTop = scrollTop;
      this.itemSizesById[resizedItemId] = size;
    }
    return this.getRenderState(params);
  }

  setSlotSize(params: { size: number; slot: IVScrollSlot; }) {
    const { size, slot } = params;
    if (slot === IVScrollSlot.HEADER) {
      this.headerSlotSize = size;
    } else if (slot === IVScrollSlot.FOOTER) {
      this.footerSlotSize = size;
    }
    return this.getRenderState();
  }

  private getAccumulatedItemsSize(items: IVScrollItem[]): number {
    return items.reduce((acc, current) => {
      const itemSize = this.getItemSize(current);
      acc += itemSize;
      return acc;
    }, this.headerSlotSize);
  }

  private getItemToPinIndex(pinnedItemId?: string | number): number {
    if (pinnedItemId) {
      const pinnedItem = this.isPinnedItemVisible();
      if (pinnedItem) {
        const ind = this.itemIndicesById[pinnedItemId];
        if (ind >= 0) return ind;
      }

    }
    /* NOTE: Fall back to default behaviour if pinned item is not in DOM */
    const firstVisible = this.getFirstPartiallyVisibleItem();
    if (firstVisible) {
      return this.itemIndicesById[firstVisible[this.dataKey]] + 1;
    } else {
      const firstFullyVisible = this.getFirstFullyVisibleItem();
      if (firstFullyVisible) {
        return this.itemIndicesById[firstFullyVisible[this.dataKey]];
      }
    }
    return -1;
  }

  setScrollCompensation(value: boolean) {
    this.correctScrollOnResize = value;
  }

  getItemOffset(args: { id: string | number; position: 'top' | 'bottom'; }) {
    const itemInd = this.itemIndicesById[args.id];
    const distanceToItem = this.getAccumulatedItemsSize(this.items.slice(0, itemInd));
    if (args.position === 'top') return distanceToItem;
    const itemSize = this.getItemSizeById(args.id);
    return distanceToItem + itemSize;
  }

  private compensateScrollTop(args: {
    sizeDiff: number;
    resizedItemId: string | number;
    pinnedItemId?: string | number;
  }): number {
    const { resizedItemId, sizeDiff, pinnedItemId } = args;
    const pinnedItemIndex = this.getItemToPinIndex(pinnedItemId);
    const resizedItemIndex = this.itemIndicesById[resizedItemId];

    if (pinnedItemIndex === null || pinnedItemIndex < 0) return this.scrollTop;
    if (this.pinnedItemPosition === 'top') {
      if (resizedItemIndex < pinnedItemIndex) {
        this.scrollTop += sizeDiff;
      }
    /* NOTE: for bottom positioning, need to compensate the resize of a pinned item */
    } else if (this.pinnedItemPosition === 'bottom' && resizedItemIndex <= pinnedItemIndex) {
      this.scrollTop += sizeDiff;
    }

    return this.scrollTop;
  }

  setViewportSize(size: number): IVScrollRenderState {
    this.viewportSize = size;
    return this.getRenderState();
  }

  setScrollTop(scrollTop: number): IVScrollRenderState {
    this.scrollTop = scrollTop;
    return this.getRenderState();
  }

  setScrollToItem(args: { id: string | number; }): IVScrollRenderState {
    const item = this.items.find(item => item[this.dataKey] === args.id);
    const ind = this.items.indexOf(item!);
    let scrollTop = this.getAccumulatedItemsSize(this.items.slice(0, ind));
    const position = this.pinnedItemPosition;
    if (position === 'top') {
      const maxScroll = this.getAccumulatedItemsSize(this.items) - this.viewportSize;
      if (maxScroll < scrollTop) {
        /* NOTE: handle edge case when selected item is at the end of the list and cannot be placed on top */
        this.setPinnedItemPosition('bottom');
      } else this.setPinnedItemPosition('top');
      scrollTop = Math.min(scrollTop, maxScroll);
    }
    if (position === 'bottom') {
      const itemSize = this.getItemSize(item!);
      const newScrollTop = scrollTop - this.viewportSize + itemSize;
      if (newScrollTop < 0) {
        /* NOTE: selected item is at the beginning of the list and cannot be placed at the bottom */
        scrollTop = 0;
        this.setPinnedItemPosition('top');
      } else {
        this.setPinnedItemPosition('bottom');
        scrollTop = newScrollTop;
      }
    }
    return this.getRenderState({ scrollTop });
  }

  /** `setItems` helpers */
  private mapper = (item: IVScrollItem) => item[this.dataKey];

  private addItem(resizedItemId: string | number) {
    Object.assign(this.itemSizesById, { [resizedItemId]: 0 });
    this.setItemSize({
      resizedItemId,
      size: this.defaultItemSize,
      pinnedItemId: this.pinnedItemId,
    });
  }

  private deleteItem(resizedItemId: string | number) {
    this.setItemSize({
      resizedItemId,
      size: 0,
      pinnedItemId: this.pinnedItemId,
    });

    delete this.itemSizesById[resizedItemId];
  }

  setItems(oldItems: IVScrollItem[], newItems: IVScrollItem[]) {
    const oldIds = oldItems.map(this.mapper);
    const newIds = newItems.map(this.mapper);
    const addedItems = _.difference(newIds, oldIds);
    const deletedItems = _.difference(oldIds, newIds);

    deletedItems.forEach(item => {
      this.deleteItem(item);
    });

    this.items = newItems;
    this.itemIndicesById = getItemIndices(this.items, this.dataKey);

    addedItems.forEach(item => {
      this.addItem(item);
    });

    return this.getRenderState({ scrollTop: this.scrollTop });
  }

  private getRenderState(params: { scrollTop?: number; } = {}): IVScrollRenderState {
    if (params.scrollTop !== undefined) {
      this.scrollTop = params.scrollTop;
    }
    const offsetTopObj: IVScrollOffsetTop = this.getOffsetTop();
    const nItemsInViewport = this.getNItemsInViewport({ offsetTop: offsetTopObj });
    const offsetBottomObj = this.getOffsetBottom({ offsetTop: offsetTopObj, nItemsInViewport });
    const nItemsInViewportWithBuffer = this.getNItemsInViewportWithBuffer({
      offsetTop: offsetTopObj,
      offsetBottom: offsetBottomObj,
      nItemsInViewport,
    });
    const visibleItems = this.getVisibleItems({
      offsetTop: offsetTopObj,
      nItemsInViewportWithBuffer,
    });
    this.visibleItems = visibleItems;

    return {
      visibleItems,
      scrollTop: this.scrollTop,
      offsetTopPx: offsetTopObj.offsetTopDistanceBuffer,
      offsetTopItems: offsetTopObj.offsetTopItemsBuffer,
      offsetBottomPx: offsetBottomObj.offsetBottomDistanceBuffer,
      offsetBottomItems: offsetBottomObj.offsetBottomItemsBuffer,
      isHeaderVisible: offsetTopObj.isHeaderSlotVisible,
      isFooterVisible: offsetBottomObj.isFooterSlotVisible,
    };
  }

  getOffsetTop() {
    const isHeaderSlotVisible = this.scrollTop <= this.headerSlotSize;
    if (isHeaderSlotVisible) {
      return {
        isHeaderSlotVisible,
        offsetTopItems: 0,
        offsetTopDistance: 0,
        offsetTopItemsBuffer: 0,
        offsetTopDistanceBuffer: 0,
      };
    }

    let offsetTopItems = 0;
    /* Add header height to the cumulative height of items when it is hidden */
    let offsetTopDistance = this.headerSlotSize;
    /* Store items' sizes for later backtracking */
    const nLastSizes: number[] = [];

    /* Iterate over items' heights, while they are fully above viewport */
    for (;;) {
      const item = this.items[offsetTopItems];
      if (!item) {
        break;
      }

      const size = this.getItemSize(item);
      if (offsetTopDistance + size >= this.scrollTop) {
        break;
      }
      offsetTopItems++;
      offsetTopDistance += size;
      nLastSizes.push(size);
    }

    /* Iterate up to "buffer" items back to determine offset values for virtual viewport */
    const bufferTop = Math.min(offsetTopItems, this.buffer);
    const offsetTopItemsBuffer = offsetTopItems - bufferTop;
    let offsetTopDistanceBuffer = offsetTopDistance;

    for (let i = 0; i < bufferTop; i++) {
      const size = nLastSizes[nLastSizes.length - 1 - i];
      offsetTopDistanceBuffer -= size;
    }

    this.offsetTopDistanceBuffer = offsetTopDistanceBuffer;

    return {
      isHeaderSlotVisible,
      offsetTopItems,
      offsetTopDistance,
      offsetTopItemsBuffer,
      offsetTopDistanceBuffer,
    };
  }

  /** Calculate how many items, at least partially, will fit on user's screen */
  getNItemsInViewport(args?: { offsetTop: IVScrollOffsetTop; }) {
    const offsetTop = args?.offsetTop ?? this.getOffsetTop();
    let offsetIndex = offsetTop.offsetTopItems;
    /* It's possible that the first visible item only partially fits on screen,
       so we increase available area by this hidden part. */
    let sizeInViewport = offsetTop.offsetTopDistance - this.scrollTop;

    do {
      const item = this.items[offsetIndex];
      /* This is possible when footer is present */
      if (!item) {
        break;
      }

      sizeInViewport += this.getItemSize(item);
      offsetIndex++;
    } while (sizeInViewport < this.viewportSize);
    return offsetIndex - offsetTop.offsetTopItems;
  }

  /**
   * Calculate how many items are available after last item that fits inside viewport,
   * as well as bottom offset for virtual viewport.
   */
  getOffsetBottom(args: {
    offsetTop?: IVScrollOffsetTop;
    nItemsInViewport?: number;
  } = {}) {
    const offsetTop = args.offsetTop ?? this.getOffsetTop();
    const nItemsInViewport = args.nItemsInViewport ?? this.getNItemsInViewport();
    /* How many items are above viewport + at least partially inside viewport */
    const offsetBottomIndex = offsetTop.offsetTopItems
      + nItemsInViewport;
    const offsetBottomItems = this.items.length - offsetBottomIndex;

    let offsetBottomDistance = 0;
    const nLastSizes: number[] = [];

    /* Add sizes of the remaining items */
    for (let i = offsetBottomIndex; i < this.items.length; i++) {
      const item = this.items[i];
      const size = this.getItemSize(item);

      offsetBottomDistance += size;
      nLastSizes.push(size);
    }

    /* Iterate up to "buffer" items back, to include them in virtual viewport */
    const bufferBottom = Math.min(offsetBottomItems, this.buffer);
    const offsetBottomItemsBuffer = offsetBottomItems - bufferBottom;
    let offsetBottomDistanceBuffer = offsetBottomDistance;

    for (let i = 0; i < bufferBottom; i++) {
      const size = nLastSizes[nLastSizes.length - 1 - i];
      offsetBottomDistanceBuffer -= size;
    }

    const isFooterSlotVisible = offsetBottomItemsBuffer === 0;

    /* Add height of a hidden footer as an additional padding */
    if (!isFooterSlotVisible) {
      offsetBottomDistance += this.footerSlotSize;
      offsetBottomDistanceBuffer += this.footerSlotSize;
    }

    return {
      isFooterSlotVisible,
      offsetBottomItems,
      offsetBottomDistance,
      offsetBottomItemsBuffer,
      offsetBottomDistanceBuffer,
    };
  }

  /** How many items should be rendered, including buffer items outside viewport */
  getNItemsInViewportWithBuffer(args: {
    offsetTop?: IVScrollOffsetTop;
    offsetBottom?: IVScrollOffsetBottom;
    nItemsInViewport?: number;
  } = {}) {
    const offsetTop = args.offsetTop ?? this.getOffsetTop();
    const offsetBottom = args.offsetBottom ?? this.getOffsetBottom();
    const nItemsInViewport = args.nItemsInViewport ?? this.getNItemsInViewport();

    const bufferTop = offsetTop.offsetTopItems
      - offsetTop.offsetTopItemsBuffer;
    const bufferBottom = offsetBottom.offsetBottomItems
      - offsetBottom.offsetBottomItemsBuffer;
    return nItemsInViewport + bufferTop + bufferBottom;
  }

  getVisibleItems(args: {
    offsetTop?: IVScrollOffsetTop;
    nItemsInViewportWithBuffer?: number;
  } = {}) {
    const offsetTop = args.offsetTop ?? this.getOffsetTop();
    const nItemsInViewportWithBuffer = args.nItemsInViewportWithBuffer ?? this.getNItemsInViewportWithBuffer();

    const sliceFrom = offsetTop.offsetTopItemsBuffer;
    const sliceTo = sliceFrom + nItemsInViewportWithBuffer;
    return this.items.slice(sliceFrom, sliceTo);
  }

  getBufferTopItems() {
    const offsetTop = this.getOffsetTop();
    const { offsetTopItems } = offsetTop;
    const nBuffer = Math.min(offsetTopItems, this.buffer);
    const topItems = this.items.slice(0, offsetTopItems);
    return nBuffer > 0 ? topItems.slice(-nBuffer) : [];
  }

  getFirstPartiallyVisibleItem(): IVScrollItem | null {
    let accumulatedSizes = this.headerSlotSize + this.offsetTopDistanceBuffer;
    let index = 0;
    for (;;) {
      const item = this.visibleItems[index];
      if (!item) return null;
      const itemSize = this.getItemSize(item);
      const newSize = accumulatedSizes + itemSize;
      if (!this.scrollTop || newSize === this.scrollTop) {
        return null;
      } else if (newSize < this.scrollTop) {
        accumulatedSizes += itemSize;
        index++;
      } else return item;
    }
  }

  getFirstFullyVisibleItem(isBottomVisible = true): IVScrollItem | null {
    let accumulatedSizes = this.headerSlotSize + this.offsetTopDistanceBuffer;
    let index = 0;
    const offsetWithViewport = this.scrollTop + this.viewportSize;
    for (;;) {
      const item = this.visibleItems[index];
      if (!item) return null;
      const itemSize = this.getItemSize(item);
      const newSize = accumulatedSizes + itemSize;
      const visibleBottom = isBottomVisible ? newSize <= offsetWithViewport : true;
      if (accumulatedSizes >= this.scrollTop && visibleBottom) {
        return item;
      } else if (newSize <= offsetWithViewport) {
        accumulatedSizes += itemSize;
        index++;
      } else return null;
    }
  }

  getLastPartiallyVisibleItem(): IVScrollItem | null {
    let accumulatedSizes = this.headerSlotSize + this.offsetTopDistanceBuffer;
    let index = 0;
    for (;;) {
      const item = this.visibleItems[index];
      if (!item) return null;
      const itemSize = this.getItemSize(item);
      const newSize = accumulatedSizes + itemSize;
      const offsetWithViewport = this.scrollTop + this.viewportSize;
      if (newSize < offsetWithViewport) {
        accumulatedSizes = newSize;
        index++;
      } else if (accumulatedSizes > this.scrollTop && newSize > offsetWithViewport) {
        return item;
      } else {
        return null;
      }
    }
  }

  getLastFullyVisibleItem(isTopVisible = true): IVScrollItem | null {
    let accumulatedSizes = this.headerSlotSize + this.offsetTopDistanceBuffer;
    let index = 0;
    const offsetWithViewport = this.scrollTop + this.viewportSize;
    let lastFullyVisible = null;
    while (index < this.visibleItems.length && accumulatedSizes <= offsetWithViewport) {
      const item = this.visibleItems[index];
      if (!item) return null;
      const itemSize = this.getItemSize(item);
      const newSize = accumulatedSizes + itemSize;
      const visibleTop = isTopVisible ? accumulatedSizes >= this.scrollTop : true;
      if (visibleTop && newSize <= offsetWithViewport) {
        lastFullyVisible = item;
      }
      accumulatedSizes = newSize;
      index ++;
    }
    return lastFullyVisible;
  }

  getItemById(id: string | number) {
    return this.items.find(item => item[this.dataKey] === id);
  }

  getItemSize = (item: IVScrollItem): number => {
    const itemId = this.getItemId(item);
    return this.getItemSizeById(itemId);
  };

  getItemSizeById(itemId: string | number): number {
    return this.itemSizesById[itemId] ?? this.defaultItemSize;
  }

  getItemId(item: IVScrollItem): string | number {
    return item[this.dataKey];
  }

  getViewportSize(): number {
    return this.viewportSize;
  }

  getItemOffsetTop(id: string): number {
    let result = 0;
    const item = this.getItemById(id);
    if (item) {
      const index = this.items.indexOf(item);
      for (let i = 0; i < index; i++) {
        result += this.getItemSize(this.items[i]);
      }
    }
    return result;
  }
}
